import { CommonModule } from '@angular/common';
import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { App, AppInfo, URLOpenListenerEvent } from '@capacitor/app';
import { IonicModule, NavController, Platform } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';

import { AuthService } from '#services/auth/auth.service';
import { ErrorService } from '#services/error/error.service';
import { RoutePaths } from './app.routes';
import { ProfileService } from '#services/profile/profile.service';
import { PushNotificationService } from '#services/push-notification/push-notification.service';
import { PurchaseService } from '#services/purchase/purchase.service';
import { LocalNotificationService } from '#services/local-notification/local-notification.service';
import { AppReloadService } from '#services/app-reload/app-reload.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: true,
  imports: [IonicModule, CommonModule],
})
export class AppComponent implements OnInit, OnDestroy {
  private subscription = new Subscription();

  constructor(
    private zone: NgZone,
    private router: Router,
    private platform: Platform,
    private authService: AuthService,
    private errorService: ErrorService,
    private navCtrl: NavController,
    private profileService: ProfileService,
    private pushNotificationService: PushNotificationService,
    private purchaseService: PurchaseService,
    private localNotificationService: LocalNotificationService,
    private appReloadService: AppReloadService
  ) {
    this.subscription.add(this.errorStatus$Subscriber());
    this.subscription.add(this.logoutEventSubscriber());
  }

  async ngOnInit() {
    // Installed before anything can navigate, so a stale bundle is caught on the first
    // lazy route the user reaches after a deploy.
    this.appReloadService.watchForStaleBundle();

    await this.platform.ready();

    if (Capacitor.isNativePlatform()) {
      const appInfo: AppInfo = await App.getInfo();

      this.profileService.setAppInfo(appInfo);

      if (Capacitor.getPlatform() === 'android') {
        // Auto-adapt status bar text/icons based on device dark/light mode.
        await StatusBar.setStyle({ style: Style.Default });
      }

      // Initializing purchases for native platforms.
      // Web will require a user ID which is set after login.
      await this.purchaseService.init();
    }

    // Add App Listeners
    this.addAppListeners();

    await this.pushNotificationService.init();
    await this.localNotificationService.init();
  }

  async ngOnDestroy() {
    this.subscription.unsubscribe();
    this.appReloadService.stopWatching();
    await this.pushNotificationService.unregisterNotifications();
  }

  private errorStatus$Subscriber() {
    return this.errorService.errorStatus$.subscribe(async (status) => {
      if (status === 401) {
        this.authService.signOut(true, async () => {
          await this.navCtrl.navigateRoot(RoutePaths.signIn, {
            animationDirection: 'back',
            replaceUrl: true,
          });
        });
      }
    });
  }

  private logoutEventSubscriber() {
    return this.authService.logoutEvent.subscribe(async () => {
      await this.purchaseService.logOutCustomerInfo();
      this.profileService.reset();
    });
  }

  /**
   * Adds listeners for app-specific events.
   *
   * This method sets up a listener for the 'appUrlOpen' event, which is triggered when the app is opened via a URL.
   * It extracts the slug from the URL and navigates to the corresponding route within the Angular application.
   *
   * The method ensures that the navigation occurs within the Angular zone, allowing Angular to properly detect changes.
   *
   * @private
   */
  private addAppListeners() {
    App.addListener('appUrlOpen', async (event: URLOpenListenerEvent) => {
      this.zone.run(async () => {
        /**
         * Extracts the slug from the given event URL.
         * The URL is expected to contain '://app' or '://app-dev' as a delimiter.
         */
        let slug = '/';
        /**
         * Determines the base URL for the app, which can be configured via environment variables.
         * This can differ from the app-specific URL scheme.
         */
        const appUrl = environment.appRedirectUrl;

        if (event.url.includes(appUrl)) {
          slug = event.url.split(appUrl).pop() ?? '/';
        } else {
          slug = event.url.split('://app').pop() ?? '/';
        }

        const url = new URL(event.url);
        const params = new URLSearchParams(url.search);

        if (params.has('action')) {
          // Set the session on OAuth redirect.
          const result = await this.authService.setSession(event.url);

          if (result) {
            window.location.replace(
              `${window.location.protocol}//${window.location.hostname}${slug}`
            );
          }
        } else if (slug) {
          await this.router.navigateByUrl(slug, { replaceUrl: true });
        }
      });
    });
  }
}
